import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

// Sessionstorage flag so we don't re-fire on every internal re-render of the same URL.
const SENT = "md-cta-sent";

/**
 * Reads `?source=...` and `?package=...` from the URL on mount + on every
 * pathname/search change. When present we POST a single tracking event to the
 * backend (fire-and-forget), then remember we've already fired for this
 * URL in this tab.
 *
 * Mount this once near the root of the React tree so every navigation flushes
 * its CTA params before the user moves on.
 */
export function useCtaTracker() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const source = params.get("source");
    const pkg = params.get("package");
    if (!source && !pkg) return;

    // Clean the tracking param out of the visible URL once captured.
    const stripSource = () => {
      if (!source) return;
      params.delete("source");
      navigate({ pathname, search: params.toString() }, { replace: true });
    };

    const key = `${pathname}${search}`;
    let already;
    try { already = sessionStorage.getItem(SENT) === key; } catch { already = false; }
    if (already) { stripSource(); return; }

    api.post("/analytics/track", {
      source: source || null,
      package: pkg || null,
      page: pathname,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    }).catch((err) => {
      // Never block UX on a tracking failure
      console.warn("[cta] track failed:", err?.message || err);
    });

    try { sessionStorage.setItem(SENT, key); } catch { /* ignore */ }
    stripSource();
  }, [pathname, search]);
}
